import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {jwtDecode} from 'jwt-decode';
import { DataService } from './data.service';
import { AuthUtils } from '../auth/auth.utils';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private dataService: DataService
    ) {}

    get accessToken(): string | null {
        return localStorage.getItem('token');
    }

    set accessToken(value: any) {
        localStorage.setItem('token', value);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: {
        username: string;
        password: string;
    }): Observable<any> {
        const url = environment.baseURL + '/endpoint/auth/dologin';
        const params = {
            username: credentials.username,
            password: credentials.password,
            appID: environment.appID,
            action: 'dologin',
        };

        return this._httpClient.post(url, params).pipe(
            switchMap((response: any) => {
                let token = response.token as string;
                // set token
                if (token.split('.').length > 3) {
                    const splitted = token.split('.');
                    splitted.pop();
                    token = splitted.join('.');
                }
                this.setToken(token);

                this._authenticated = true;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        const url = environment.baseURL + '/endpoint/auth/refreshToken';

        return this._httpClient
            .post(url, {
                tokenID: localStorage.getItem('tokenID'),
                appID: environment.appID,
                action: 'refreshToken',
            })
            .pipe(
                catchError(() => {
                    // Return false
                    return of(false);
                }),
                switchMap((response: any) => {

                    let token = response.token as string;
                    // set token
                    // if (token.split('.').length > 3) {
                    //     const splitted = token.split('.');
                    //     splitted.pop();
                    //     token = splitted.join('.');
                    // }
                    this.setToken(token);

                    // Return true
                    return of(true);
                })
            );
    }

    /**
     * Sign out
     */
    async signOut(): Promise<any> {
        // Remove the access token from the local storage
        // const response = await firstValueFrom(this.signOutRequest());
        // return response;
    }

    signOutRequest() {
        const url = environment.baseURL + '/endpoint/auth';
        const params = {
            action: 'dologout',
        };
        return this._httpClient.post(url, params).pipe(
            finalize(() => {
                localStorage.clear();
            }),
            catchError(() => {
                return of(true);
            })
        );
    }

    setToken(token: string): void {
        const tokenObj = jwtDecode(token) as any;

        localStorage.setItem('token', token);
        localStorage.setItem('tokenID', tokenObj.jti);

        this.accessToken = token;

        for (const key in tokenObj.data) {
            if (Object.prototype.hasOwnProperty.call(tokenObj.data, key)) {
                localStorage.setItem(key, tokenObj.data[key]);
            }
        }
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (this.accessToken) {
            return of(true);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken as string)) {
            return this.signInUsingToken();
            // return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
